'use client'

import { useEffect, useState } from 'react'
import { usePathname } from 'next/navigation'
import { motion } from 'framer-motion'
import { Check, ChevronRight, Coins, Gift, Medal, ShoppingBag, Star, Ticket, TrendingUp } from 'lucide-react'
import { useAuth } from '@/lib/commerce/hooks'
import { getLoyaltyTiersApi, getLoyaltySummaryApi } from '@/lib/commerce/services/loyalty-api'
import { navigateUrl } from '@/utils/client-request'
import { classNames, formatImageURL, formatNumber, formatPrice, isCustomerAuthActive } from '@/utils/util'
import { STATUS_LABELS } from '@/utils/order'
import ConvertVoucherModal from './ConvertVoucherModal'

export default function Loyalty({ vendor }: { vendor: any }) {
  const pathname = usePathname()
  const { session, isLoggedIn } = useAuth()

  const [program, setProgram] = useState<any>(null)
  const [loadingProgram, setLoadingProgram] = useState(true)
  const [summary, setSummary] = useState<any>(null)
  const [loadingSummary, setLoadingSummary] = useState(false)
  const [convertOpen, setConvertOpen] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)

  const tiers = program?.tiers || []
  const ctx = { vendorId: vendor?.id, session: session || undefined }

  useEffect(() => {
    let cancelled = false
    getLoyaltyTiersApi({ vendorId: vendor?.id }).then((programRes: any) => {
      if (cancelled) return
      setProgram(programRes?.data || null)
    }).catch(() => { }).finally(() => { if (!cancelled) setLoadingProgram(false) })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!isLoggedIn) return
    let cancelled = false
    setLoadingSummary(true)
    getLoyaltySummaryApi(ctx).then((summaryRes) => {
      if (cancelled) return
      setSummary(summaryRes?.data || null)
    }).catch(() => { }).finally(() => { if (!cancelled) setLoadingSummary(false) })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, refreshKey])

  const benefitLabel = (benefit: any) => {
    if (benefit.type === 'percentDiscount') return `${benefit.valuePercent}% хөнгөлөлт`
    if (benefit.type === 'fixedDiscount') return `${formatPrice(benefit.valueAmount)} хөнгөлөлт`
    if (benefit.type === 'freeShipping') return 'Үнэгүй хүргэлт'
    if (benefit.type === 'shippingDiscount') return `Хүргэлт ${benefit.valuePercent}% хөнгөлөлттэй`
    return 'Нэмэлт урамшуулал'
  }

  const progressPercent = summary?.nextTier
    ? Math.min(100, Math.round((summary.qualifyingSpend / summary.nextTier.minAnnualSpend) * 100))
    : 100

  const steps = [
    { icon: ShoppingBag, t: 'Худалдан авалт бүрээс оноо цуглуул', s: `Захиалга "${STATUS_LABELS[program?.awardStatus] || STATUS_LABELS.paid}" төлөвт орох бүрд түвшнийхээ хувиар оноо орно.` },
    { icon: TrendingUp, t: 'Түвшин ахиж, илүү урамшуулал ав', s: 'Жилийн худалдан авалтаараа дээд түвшинд хүрч, илүү хөнгөлөлт эдлээрэй.' },
    { icon: Ticket, t: 'Оноогоо ваучер болгон ашигла', s: 'Цуглуулсан оноогоо ваучер болгон дараагийн захиалгадаа хэрэглэнэ.' },
  ]

  return (
    <div>
      <div className="mx-auto w-full max-w-[1280px] px-4 lg:px-8 pt-6 md:pt-10 pb-16">
        <div className="hidden md:flex items-center gap-1.5 text-[12px]" style={{ color: 'rgba(255,255,255,0.55)' }}>
          <a href={navigateUrl('/', pathname)} className="hover:text-white transition-colors">Нүүр</a>
          <ChevronRight className="h-3 w-3" style={{ opacity: 0.5 }} />
          <span className="text-white font-medium">Лояалти хөтөлбөр</span>
        </div>

        <div className="mx-auto max-w-190">
          <div className="mt-6 relative overflow-hidden rounded-2xl p-6 sm:p-9" style={{ background: 'radial-gradient(800px 400px at 80% 0%, rgba(255,255,255,0.08), transparent 60%), radial-gradient(600px 400px at 0% 100%, rgba(255,255,255,0.04), transparent 60%), #0c0c10', border: '1px solid rgba(255,255,255,0.13)' }}>
            <div className="relative flex items-center gap-5 sm:gap-6">
              <div className="h-16 w-16 sm:h-20 sm:w-20 shrink-0 rounded-full grid place-items-center" style={{ background: 'white', color: '#0a0a0c' }}>
                <Medal className="h-8 w-8 sm:h-10 sm:w-10" strokeWidth={2} />
              </div>
              <div className="min-w-0">
                <h1 className="font-bold tracking-[-0.025em] leading-[1.02] text-white" style={{ fontFamily: 'Gilroy, Inter, sans-serif', fontSize: 'clamp(28px, 4.5vw, 44px)' }}>
                  Лояалти хөтөлбөр
                </h1>
              </div>
            </div>

            <p className="relative mt-5 text-[14.5px] leading-relaxed max-w-2xl" style={{ color: 'rgba(255,255,255,0.7)' }}>
              Худалдан авалт бүрээсээ оноо цуглуулж, түвшин ахих тусам илүү их хөнгөлөлт аваарай.
              Цуглуулсан оноогоо ваучер болгон дараагийн захиалгадаа ашиглах боломжтой.
            </p>
          </div>

          {!isLoggedIn && isCustomerAuthActive(vendor) && loadingProgram && (
            <div className="mt-5 h-36 w-full rounded-2xl animate-pulse" style={{ background: 'rgba(255,255,255,0.04)' }} />
          )}

          {!isLoggedIn && isCustomerAuthActive(vendor) && !loadingProgram && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="mt-5 rounded-2xl p-6 sm:p-7" style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)' }}>
              <div className="flex flex-col sm:flex-row sm:items-center gap-5">
                <div className="h-14 w-14 shrink-0 rounded-full grid place-items-center" style={{ background: 'rgba(16,185,129,0.18)', color: '#34d399', border: '1px solid rgba(16,185,129,0.3)' }}>
                  <Gift className="h-7 w-7" strokeWidth={2} />
                </div>
                <div className="flex-1 min-w-0">
                  {program?.welcomeBonusPoints > 0 ? (
                    <>
                      <div className="text-2xl sm:text-3xl font-bold tracking-tight tabular-nums" style={{ fontFamily: 'Gilroy, Inter, sans-serif', color: '#34d399' }}>+{formatNumber(program.welcomeBonusPoints)} оноо</div>
                      <p className="mt-1 text-sm" style={{ color: 'rgba(255,255,255,0.6)' }}>Бүртгэлээ үүсгэж үнэгүй оноо аваад, ваучер болгон ашиглаарай.</p>
                    </>
                  ) : (
                    <>
                      <div className="text-xl sm:text-2xl font-bold tracking-tight text-white" style={{ fontFamily: 'Gilroy, Inter, sans-serif' }}>Оноо цуглуулж эхлээрэй</div>
                      <p className="mt-1 text-sm" style={{ color: 'rgba(255,255,255,0.6)' }}>Бүртгүүлсэн худалдан авагч бүр худалдан авалтаасаа оноо цуглуулна.</p>
                    </>
                  )}
                </div>
                <div className="flex items-center gap-2.5 shrink-0">
                  <a href={navigateUrl('/register', pathname)} className="h-11 px-5 rounded-full inline-flex items-center justify-center text-[13px] font-semibold transition-opacity hover:opacity-90" style={{ background: 'white', color: '#0a0a0c' }}>Бүртгүүлэх</a>
                  <a href={navigateUrl(`/login?redirect=/loyalty`, pathname)} className="h-11 px-5 rounded-full inline-flex items-center justify-center text-[13px] font-medium transition-colors hover:bg-white/5" style={{ border: '1px solid rgba(255,255,255,0.2)', color: 'white' }}>Нэвтрэх</a>
                </div>
              </div>
            </motion.div>
          )}

          {isLoggedIn && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="mt-5">
              {loadingSummary && !summary ? (
                <div className="h-40 w-full rounded-2xl animate-pulse" style={{ background: 'rgba(255,255,255,0.04)' }} />
              ) : summary ? (
                <div className="rounded-2xl p-5 sm:p-6" style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)' }}>
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      {summary.currentTier?.icon ? (
                        <img src={formatImageURL(summary.currentTier.icon)} alt={summary.currentTier.name} className="size-12 rounded-full object-cover" style={{ border: '1px solid rgba(255,255,255,0.13)' }} />
                      ) : (
                        <div className="size-12 rounded-full flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.07)' }}><Medal className="size-6" style={{ color: 'rgba(255,255,255,0.55)' }} /></div>
                      )}
                      <div>
                        <div className="text-[10px] font-semibold uppercase tracking-[0.22em]" style={{ color: '#a8a8b3' }}>Таны түвшин</div>
                        <div className="text-lg font-semibold text-white mt-0.5" style={{ fontFamily: 'Gilroy, Inter, sans-serif' }}>{summary.currentTier?.name || '—'}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] font-semibold uppercase tracking-[0.22em]" style={{ color: '#a8a8b3' }}>Онооны үлдэгдэл</div>
                      <div className="text-2xl font-bold tabular-nums text-white mt-0.5" style={{ fontFamily: 'Gilroy, Inter, sans-serif' }}>{formatNumber(summary.balance || 0)}</div>
                    </div>
                    <button type="button" onClick={() => setConvertOpen(true)} disabled={(summary.balance || 0) < (program?.minPointsToConvert || 1)} className="h-11 px-5 rounded-full text-[13px] font-semibold inline-flex items-center gap-2 transition-opacity hover:opacity-90 disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed" style={{ background: 'white', color: '#0a0a0c' }}>
                      <Ticket className="h-4 w-4" /> Ваучер болгох
                    </button>
                  </div>
                  <div className="mt-5">
                    {summary.nextTier ? (
                      <>
                        <div className="flex items-center justify-between text-[13px] mb-1.5">
                          <span style={{ color: 'rgba(255,255,255,0.55)' }}>Энэ жилийн худалдан авалт: <span className="font-medium text-white">{formatPrice(summary.qualifyingSpend || 0)}</span></span>
                          <span style={{ color: 'rgba(255,255,255,0.55)' }}>{summary.nextTier.name}: {formatPrice(summary.nextTier.minAnnualSpend)}</span>
                        </div>
                        <div className="h-1.5 w-full rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.07)' }}>
                          <div className="h-full rounded-full transition-all" style={{ width: `${progressPercent}%`, background: 'white' }} />
                        </div>
                        <p className="text-[12px] mt-1.5" style={{ color: 'rgba(255,255,255,0.55)' }}>{summary.nextTier.name} түвшинд хүрэхэд {formatPrice(summary.nextTier.remainingSpend)} дутуу байна</p>
                      </>
                    ) : (
                      <p className="text-[13.5px] flex items-center gap-1.5" style={{ color: 'rgba(255,255,255,0.7)' }}><Star className="size-4" style={{ color: '#fbbf24' }} /> Та хамгийн дээд түвшинд байна. Энэ жилийн худалдан авалт {formatPrice(summary.qualifyingSpend || 0)}</p>
                    )}
                  </div>
                  <a href={navigateUrl('/points', pathname)} className="mt-4 text-[13.5px] font-medium text-white inline-flex items-center gap-0.5 hover:opacity-70 transition-opacity">
                    Онооны түүх үзэх
                    <ChevronRight className="size-4" />
                  </a>
                </div>
              ) : null}
            </motion.div>
          )}

          <div className="mt-10 pt-8" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
            <div className="text-[10px] font-semibold uppercase tracking-[0.22em]" style={{ color: '#a8a8b3' }}>Хэрхэн ажилладаг вэ</div>
            <ul className="mt-5 space-y-5">
              {steps.map((step, index) => {
                const Icon = step.icon
                return (
                  <li key={index} className="flex gap-4">
                    <div className="mt-0.5 h-8 w-8 shrink-0 grid place-items-center rounded-full" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.07)', color: 'white' }}>
                      <Icon className="h-4 w-4" strokeWidth={2} />
                    </div>
                    <div className="min-w-0">
                      <div className="text-[15px] font-semibold text-white">{step.t}</div>
                      <div className="mt-0.5 text-[13.5px] leading-relaxed" style={{ color: 'rgba(255,255,255,0.6)' }}>{step.s}</div>
                    </div>
                  </li>
                )
              })}
            </ul>
          </div>
        </div>

        <div className="mx-auto max-w-5xl mt-10 pt-8" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
          <div className="text-[10px] font-semibold uppercase tracking-[0.22em]" style={{ color: '#a8a8b3' }}>Түвшингүүд</div>
          {loadingProgram && (
            <div className="mt-5 grid gap-5 md:grid-cols-3">
              {Array.from({ length: 3 }).map((_, index) => (
                <div key={index} className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)' }}>
                  <div className="flex items-center gap-3">
                    <div className="size-12 shrink-0 rounded-full animate-pulse" style={{ background: 'rgba(255,255,255,0.06)' }} />
                    <div className="flex-1 min-w-0 space-y-2">
                      <div className="h-5 w-24 rounded animate-pulse" style={{ background: 'rgba(255,255,255,0.06)' }} />
                      <div className="h-3 w-32 rounded animate-pulse" style={{ background: 'rgba(255,255,255,0.04)' }} />
                    </div>
                  </div>
                  <div className="mt-4 space-y-2.5">
                    <div className="h-4 w-full rounded animate-pulse" style={{ background: 'rgba(255,255,255,0.04)' }} />
                    <div className="h-4 w-5/6 rounded animate-pulse" style={{ background: 'rgba(255,255,255,0.04)' }} />
                    <div className="h-4 w-2/3 rounded animate-pulse" style={{ background: 'rgba(255,255,255,0.04)' }} />
                  </div>
                </div>
              ))}
            </div>
          )}
          {!loadingProgram && (
          <div className={classNames('mt-5 grid gap-5', tiers.length >= 3 ? 'md:grid-cols-3' : tiers.length === 2 ? 'md:grid-cols-2' : 'max-w-lg')}>
            {tiers.map((tier: any, index: number) => {
              const isCurrent = summary?.currentTier?.id === tier.id
              return (
                <motion.div key={tier.id} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }} viewport={{ once: true }} className="rounded-2xl p-5 flex flex-col" style={{ background: 'rgba(255,255,255,0.035)', border: isCurrent ? '1px solid rgba(255,255,255,0.8)' : '1px solid rgba(255,255,255,0.07)' }}>
                  <div className="flex items-center gap-3">
                    {tier.icon ? (
                      <img src={formatImageURL(tier.icon)} alt={tier.name} className="size-12 rounded-full object-cover" style={{ border: '1px solid rgba(255,255,255,0.13)' }} />
                    ) : (
                      <div className="size-12 rounded-full flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.07)' }}><Medal className="size-6" style={{ color: 'rgba(255,255,255,0.55)' }} /></div>
                    )}
                    <div>
                      <div className="font-semibold text-lg text-white flex items-center gap-2" style={{ fontFamily: 'Gilroy, Inter, sans-serif' }}>
                        {tier.name}
                        {isCurrent && <span className="text-[10.5px] font-semibold px-2 py-0.5 rounded-full" style={{ background: 'white', color: '#0a0a0c' }}>Таны түвшин</span>}
                      </div>
                      <div className="text-[13px]" style={{ color: 'rgba(255,255,255,0.55)' }}>{tier.minAnnualSpend > 0 ? `${formatPrice(tier.minAnnualSpend)}-с дээш` : 'Эхлэх түвшин'}</div>
                    </div>
                  </div>
                  <ul className="mt-4 space-y-2 text-[13.5px] flex-1" style={{ color: 'rgba(255,255,255,0.85)' }}>
                    <li className="flex items-start gap-2">
                      <Coins className="size-4 mt-0.5 shrink-0" style={{ color: 'rgba(255,255,255,0.5)' }} />
                      <span>{formatNumber(tier.spendPerPoint)}₮ тутамд {formatNumber(tier.pointsPerUnit)} оноо</span>
                    </li>
                    {tier.reviewRewardPoints > 0 && (
                      <li className="flex items-start gap-2">
                        <Star className="size-4 mt-0.5 shrink-0" style={{ color: 'rgba(255,255,255,0.5)' }} />
                        <span>Сэтгэгдэл бүрд {formatNumber(tier.reviewRewardPoints)} оноо</span>
                      </li>
                    )}
                    {(tier.benefits || []).map((benefit: any) => (
                      <li key={benefit.id} className="flex items-start gap-2">
                        <Check className="size-4 mt-0.5 shrink-0" style={{ color: '#34d399' }} />
                        <span>{benefitLabel(benefit)}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              )
            })}
          </div>
          )}
          <p className="text-[12px] mt-4 text-center" style={{ color: 'rgba(255,255,255,0.45)' }}>Түвшин жилийн худалдан авалтаар тодорхойлогдож, жил бүр шинээр эхэлнэ. Цуглуулсан оноо хадгалагдана.</p>
        </div>

      </div>

      <ConvertVoucherModal open={convertOpen} setOpen={setConvertOpen} vendor={vendor} program={program} balance={summary?.balance || 0} onConverted={() => setRefreshKey((key) => key + 1)} />
    </div>
  )
}
